import { MoreOutlined, StarFilled } from '@ant-design/icons';
import {
  Button,
  Dropdown,
  MenuProps,
  Table,
  TablePaginationConfig,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { Store } from 'models/Store';

import './StoreTable.scss';

interface StoreTableProps {
  dataSource: Store[];
  loading?: boolean;
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  onPaginationChange: (page: number, pageSize: number) => void;
  onEdit: (store: Store) => void;
  onToggleVip: (store: Store) => void;
  onToggleActive: (store: Store) => void;
  onOpenMap: (store: Store) => void;
}

function StoreTable({
  dataSource,
  loading,
  page,
  pageSize,
  total,
  pageSizeOptions = [10, 20, 50],
  onPaginationChange,
  onEdit,
  onToggleVip,
  onToggleActive,
  onOpenMap,
}: StoreTableProps) {
  const { t } = useTranslation();
  const i18nBase = 'scenes.stores.scene';

  const getRowMenu = (store: Store): MenuProps['items'] => [
    {
      key: 'edit',
      label: t(`${i18nBase}.dropdown.edit`),
      onClick: () => onEdit(store),
    },
    {
      key: 'vip',
      label: store.isVip
        ? t(`${i18nBase}.dropdown.remove_vip`)
        : t(`${i18nBase}.dropdown.mark_vip`),
      onClick: () => onToggleVip(store),
    },
    {
      key: 'active',
      danger: store.isActive,
      label: store.isActive
        ? t(`${i18nBase}.dropdown.deactivate`)
        : t(`${i18nBase}.dropdown.activate`),
      onClick: () => onToggleActive(store),
    },
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.map(String),
    showQuickJumper: false,
    position: ['bottomRight'],
    onChange: (nextPage, nextPageSize) => {
      onPaginationChange(nextPage, nextPageSize || pageSize);
    },
  };

  return (
    <div className="store-table">
      <Table<Store>
        rowKey="id"
        loading={loading}
        dataSource={dataSource}
        pagination={pagination}
        scroll={{ x: 960 }}
      >
        <Table.Column<Store>
          title={t(`${i18nBase}.table.columns.store`)}
          key="store"
          render={(_, record) => (
            <div className="store-table__store-cell">
              <div className="store-table__store-name">
                <span>{record.name}</span>
                {record.isVip && (
                  <StarFilled className="store-table__vip-icon" />
                )}
              </div>
              <div className="store-table__store-code">{record.code}</div>
            </div>
          )}
        />
        <Table.Column<Store>
          title={t(`${i18nBase}.table.columns.chain`)}
          dataIndex="chain"
          key="chain"
        />
        <Table.Column<Store>
          title={t(`${i18nBase}.table.columns.commune_region`)}
          key="commune_region"
          render={(_, record) => (
            <div className="store-table__location-cell">
              <div className="store-table__commune">
                {record.commune || '—'}
              </div>
              <div className="store-table__region-kind">
                {record.regionKind === 'CAPITAL'
                  ? t(`${i18nBase}.region_kinds.capital`)
                  : t(`${i18nBase}.region_kinds.region`)}
              </div>
            </div>
          )}
        />
        <Table.Column<Store>
          title={t(`${i18nBase}.table.columns.address`)}
          dataIndex="address"
          key="address"
          ellipsis
        />
        <Table.Column<Store>
          title={t(`${i18nBase}.table.columns.map`)}
          key="map"
          width={90}
          render={(_, record) => (
            <Button
              type="link"
              className="store-table__map-link"
              onClick={() => onOpenMap(record)}
            >
              {t(`${i18nBase}.table.view_map`)}
            </Button>
          )}
        />
        <Table.Column<Store>
          key="actions"
          width={56}
          align="right"
          render={(_, record) => (
            <Dropdown
              menu={{ items: getRowMenu(record) }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                className="store-table__more-btn"
                icon={<MoreOutlined />}
              />
            </Dropdown>
          )}
        />
      </Table>
    </div>
  );
}

export default StoreTable;
